/**
 * TradeFinder Settings tab: shapes and pure logic (no React, no fetch).
 *
 * Source: flow-signals' settings API, proxied by the engine
 * (utt-engine/src/flow_signals/settings.rs):
 * - GET {api}/flow-schema            -> FlowParam[] (UI order)
 * - GET {api}/flow-settings/{root}   -> FlowSettings (incl. per-path `layers`)
 * - PUT {api}/flow-settings/{root}   {set, unset, pin, unpin} -> FlowSettings + written/backup/reloaded;
 *                                    400 {error, path} on a bad value
 * - GET {api}/flow-tune              -> FlowTune; 404 before the first nightly tune
 * Protocol: rea-bm/bookmap/reimpl/flow-signals/PROTOCOL.md.
 */

export type FlowParamType = 'number' | 'integer' | 'bool' | 'enum';
export type FlowValue = number | boolean | string;

export interface FlowParam {
  path: string;
  module: string;
  group: string;
  label: string;
  unit: string;
  type: FlowParamType;
  options?: string[];
  min: number | null;
  max: number | null;
  step: number | null;
  default: FlowValue;
  tunable: boolean;
  help?: string;
}

export interface FlowLayer {
  default: FlowValue | null;
  user: FlowValue | null;
  user_source?: string | null;
  autotune: FlowValue | null;
  contract: FlowValue | null;
  effective: FlowValue | null;
  source: string;
  pinned: boolean;
  tunable: boolean;
}

export interface FlowSettings {
  symbol: string;
  settings?: unknown;
  sources?: Record<string, string>;
  pinned?: string[];
  autotune: string | null;
  layers: Record<string, FlowLayer>;
  // PUT response only
  root?: string;
  written?: boolean;
  backup?: string | null;
  reloaded?: unknown[];
}

export interface FlowHorizon { mean: number | null; t: number | null; n: number }
export interface FlowExcursion { mfe: number | null; mae: number | null; n?: number }
export interface FlowTarget { win: number | null; loss: number | null; n: number }

export interface FlowEdgeStats {
  reading: string | null;
  band_t?: number | null;
  best_horizon_s?: number | null;
  mean_ticks_300s: number | null;
  t: number | null;
  n_per_day: number | null;
  hit?: number | null;
  multiplier: number | null;
  adopted: boolean;
  reason: string;
  horizons?: Record<string, FlowHorizon>;
  excursions?: Record<string, FlowExcursion>;
  targets?: Record<string, FlowTarget>;
  days?: number;
  n?: number;
  param?: string | null;
  threshold?: { baseline: number; value: number } | null;
}

export interface FlowTuneDay { root: string; date: string; contract?: string; split?: string; partial?: boolean }

export interface FlowTune {
  generated: string;
  days: FlowTuneDay[] | number;
  symbols: Record<string, unknown>;
  edge: Record<string, Record<string, FlowEdgeStats>>;
  path?: string;
  loaded?: boolean;
}

export interface FlowPutBody {
  set?: Record<string, FlowValue>;
  unset?: string[];
  pin?: string[];
  unpin?: string[];
}

/** Roots the selector offers (flow-signals accepts these always). */
/**
 * Units the schema states too loosely to read right. flow-signals reports
 * `liquidity.relative_trigger` as plain "lots", next to the absolute trigger
 * (a level's resting size) in the same units, but it is the size change of
 * ONE depth update (never summed over time): a bare "lots" reads as either.
 */
const UNIT_OVERRIDES: Record<string, string> = {
  'liquidity.relative_trigger': 'lots / update',
  'liquidity.absolute_trigger': 'lots at a level',
};

/** Unit shown next to a setting's label ('' = none). */
export function displayUnit(p: Pick<FlowParam, 'path' | 'unit'>): string {
  return UNIT_OVERRIDES[p.path] ?? p.unit ?? '';
}

export const FLOW_SETTINGS_ROOTS = ['NQ', 'MNQ', 'ES', 'MES'] as const;

/** Groups the user trades on, in this order and open by default; the rest follow collapsed. */
export const PRIORITY_GROUPS = [
  'Absorption',
  'Sweep',
  'Stop Run',
  'Stop Volume',
  'Volume Spike',
  'Market Volume Imbalance',
] as const;

/** Shown right after the priority groups (the MVI signals), collapsed. */
const SECONDARY_GROUPS = ['MVI Signal 1', 'MVI Signal 2', 'MVI Signal 3'];

export interface FlowParamGroup {
  /** `module/group`: stable key and testid suffix. */
  key: string;
  module: string;
  group: string;
  params: FlowParam[];
  defaultOpen: boolean;
}

/** Schema params grouped by module/group: priority groups first (open), then the rest in schema order. */
export function groupSchema(schema: readonly FlowParam[]): FlowParamGroup[] {
  const map = new Map<string, FlowParamGroup>();
  for (const p of schema) {
    const key = `${p.module}/${p.group}`;
    let g = map.get(key);
    if (!g) {
      g = { key, module: p.module, group: p.group, params: [], defaultOpen: false };
      map.set(key, g);
    }
    g.params.push(p);
  }
  const all = [...map.values()];
  const rank = (g: FlowParamGroup): number => {
    const i = (PRIORITY_GROUPS as readonly string[]).indexOf(g.group);
    if (i >= 0) return i;
    const j = SECONDARY_GROUPS.indexOf(g.group);
    if (j >= 0) return PRIORITY_GROUPS.length + j;
    return 100;
  };
  // Stable sort keeps schema order inside the same rank.
  return all
    .map((g, i) => ({ g, i }))
    .sort((a, b) => rank(a.g) - rank(b.g) || a.i - b.i)
    .map(({ g }) => ({ ...g, defaultOpen: (PRIORITY_GROUPS as readonly string[]).includes(g.group) }));
}

export type FlowSourceBadge = 'default' | 'user' | 'autotune' | 'contract';

/**
 * Layer name (`builtin`, `user.default`, `user.symbols.MNQ`, `autotune`,
 * `user.symbols.MNQZ6`, maybe ` (pinned)` appended) to a badge.
 */
export function sourceBadge(source: string | null | undefined, root: string): FlowSourceBadge {
  const s = (source ?? '').replace(/\s*\(pinned\)\s*$/, '').trim();
  if (s === 'autotune') return 'autotune';
  if (s === 'user.default') return 'user';
  if (s.startsWith('user.symbols.')) {
    const who = s.slice('user.symbols.'.length);
    return who.toUpperCase() === root.toUpperCase() ? 'user' : 'contract';
  }
  return 'default';
}

/** Value for display: numbers trimmed, bools on/off, null as an em dash. */
export function fmtValue(v: FlowValue | null | undefined): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'on' : 'off';
  if (typeof v === 'number') {
    if (!Number.isFinite(v)) return String(v);
    return Number.isInteger(v) ? v.toLocaleString('en-US') : String(Math.round(v * 10_000) / 10_000);
  }
  return String(v);
}

/** Input text for a value (no grouping separators). */
export function valueToInput(v: FlowValue | null | undefined): string {
  if (v === null || v === undefined) return '';
  return String(v);
}

export type ParseResult = { ok: true; value: FlowValue } | { ok: false; error: string };

/** Validate an edited value against the schema (type, min/max, options) before save. */
export function parseParamValue(p: FlowParam, raw: FlowValue): ParseResult {
  if (p.type === 'bool') {
    if (typeof raw === 'boolean') return { ok: true, value: raw };
    if (raw === 'true' || raw === 'false') return { ok: true, value: raw === 'true' };
    return { ok: false, error: 'on or off' };
  }
  if (p.type === 'enum') {
    const s = String(raw);
    if ((p.options ?? []).includes(s)) return { ok: true, value: s };
    return { ok: false, error: `one of ${(p.options ?? []).join(', ')}` };
  }
  const text = typeof raw === 'number' ? String(raw) : String(raw).trim();
  if (text === '') return { ok: false, error: 'enter a value' };
  const n = Number(text);
  if (!Number.isFinite(n)) return { ok: false, error: 'not a number' };
  if (p.type === 'integer' && !Number.isInteger(n)) return { ok: false, error: 'whole number' };
  if (p.min !== null && p.min !== undefined && n < p.min) return { ok: false, error: `min ${fmtValue(p.min)}` };
  if (p.max !== null && p.max !== undefined && n > p.max) return { ok: false, error: `max ${fmtValue(p.max)}` };
  return { ok: true, value: n };
}

/** One row's pending change: a new value of yours, or remove yours. */
export type FlowDraft = { kind: 'set'; raw: FlowValue } | { kind: 'unset' };

export interface FlowDrafts {
  values: Record<string, FlowDraft>;
  /** Desired pin state where it differs from the server's. */
  pins: Record<string, boolean>;
}

export const EMPTY_DRAFTS: FlowDrafts = { values: {}, pins: {} };

export function isDirty(d: FlowDrafts): boolean {
  return Object.keys(d.values).length > 0 || Object.keys(d.pins).length > 0;
}

/** Per-path validation errors for the set drafts. */
export function draftErrors(d: FlowDrafts, schema: readonly FlowParam[]): Record<string, string> {
  const byPath = new Map(schema.map((p) => [p.path, p]));
  const out: Record<string, string> = {};
  for (const [path, draft] of Object.entries(d.values)) {
    if (draft.kind !== 'set') continue;
    const p = byPath.get(path);
    if (!p) {
      out[path] = 'unknown setting';
      continue;
    }
    const r = parseParamValue(p, draft.raw);
    if (!r.ok) out[path] = r.error;
  }
  return out;
}

/**
 * The one PUT body for the drafts: exactly the non-empty of
 * `set` / `unset` / `pin` / `unpin`. Throws if a draft does not validate.
 */
export function buildPutBody(d: FlowDrafts, schema: readonly FlowParam[]): FlowPutBody {
  const byPath = new Map(schema.map((p) => [p.path, p]));
  const set: Record<string, FlowValue> = {};
  const unset: string[] = [];
  const pin: string[] = [];
  const unpin: string[] = [];
  for (const [path, draft] of Object.entries(d.values)) {
    if (draft.kind === 'unset') {
      unset.push(path);
      continue;
    }
    const p = byPath.get(path);
    if (!p) throw new Error(`${path}: unknown setting`);
    const r = parseParamValue(p, draft.raw);
    if (!r.ok) throw new Error(`${path}: ${r.error}`);
    set[path] = r.value;
  }
  for (const [path, on] of Object.entries(d.pins)) (on ? pin : unpin).push(path);
  const body: FlowPutBody = {};
  if (Object.keys(set).length > 0) body.set = set;
  if (unset.length > 0) body.unset = unset.sort();
  if (pin.length > 0) body.pin = pin.sort();
  if (unpin.length > 0) body.unpin = unpin.sort();
  return body;
}

/** Whether the user's value for `path` sits on this root (so `unset` can remove it). */
export function hasRootValue(layer: FlowLayer | undefined, root: string): boolean {
  return !!layer && layer.user !== null && layer.user !== undefined && layer.user_source === `user.symbols.${root}`;
}

/** Same value? (number compare tolerant of "400" vs 400). */
export function sameValue(a: FlowValue | null | undefined, b: FlowValue | null | undefined): boolean {
  if (a === null || a === undefined || b === null || b === undefined) return a === b;
  if (typeof a === 'number' || typeof b === 'number') {
    const x = Number(a);
    const y = Number(b);
    return Number.isFinite(x) && Number.isFinite(y) && x === y && String(a).trim() !== '' && String(b).trim() !== '';
  }
  return a === b;
}

// ------------------------------------------------------------------ edge

/** Horizons (s) for the scalper curve, and the target sizes (ticks). */
export const EDGE_HORIZONS = ['60', '120', '180', '240', '300'] as const;
export const EDGE_TARGETS = ['4', '8'] as const;

/** Display order of edge groups; unknown groups follow alphabetically. */
const EDGE_ORDER = [
  'absorption', 'sweep', 'sweep_s', 'stop_run', 'stop_volume', 'volume_spike',
  'imbalance_signal1', 'imbalance_signal2', 'imbalance_signal3',
];

const EDGE_LABELS: Record<string, string> = {
  absorption: 'Absorption',
  sweep: 'Sweep',
  sweep_s: 'Sweep-S',
  stop_run: 'Stop Run',
  stop_volume: 'Stop Volume',
  volume_spike: 'Volume Spike',
  imbalance_signal1: 'MVI Signal 1',
  imbalance_signal2: 'MVI Signal 2',
  imbalance_signal3: 'MVI Signal 3',
  sweep_follow: 'Sweep Follow',
};

export function edgeLabel(group: string): string {
  return EDGE_LABELS[group] ?? group.replace(/_/g, ' ');
}

export function edgeEntries(tune: FlowTune | null, root: string): [string, FlowEdgeStats][] {
  const byGroup = tune?.edge?.[root];
  if (!byGroup) return [];
  const rank = (g: string) => {
    const i = EDGE_ORDER.indexOf(g);
    return i < 0 ? EDGE_ORDER.length : i;
  };
  return Object.entries(byGroup).sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b));
}

/** Days the tuner used for `root`: count and date span (from the file's `days` list). */
export function tuneDays(tune: FlowTune | null, root: string): { count: number; first?: string; last?: string } {
  if (!tune) return { count: 0 };
  if (typeof tune.days === 'number') return { count: tune.days };
  const dates = (Array.isArray(tune.days) ? tune.days : [])
    .filter((d) => d && d.root === root && typeof d.date === 'string')
    .map((d) => d.date)
    .sort();
  const unique = [...new Set(dates)];
  if (unique.length === 0) {
    const fromEdge = Object.values(tune.edge?.[root] ?? {}).find((e) => typeof e.days === 'number')?.days;
    return { count: fromEdge ?? 0 };
  }
  return { count: unique.length, first: unique[0], last: unique[unique.length - 1] };
}

/** Signed ticks / t with one decimal; em dash when absent. */
export function fmtSigned(v: number | null | undefined, digits = 1): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  const s = v.toFixed(digits);
  return v > 0 ? `+${s}` : s;
}

export function fmtPct(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  return `${Math.round(v * 100)}%`;
}

/** |band_t| below this = no reliable edge (PROTOCOL.md); defined once in flowSignalsStore. */
export { EDGE_T_RELIABLE } from '../../store/flowSignalsStore';
